import { z } from 'zod';
import {
  pricingAdjustmentsInputSchema,
  pricingAdjustmentsOutputSchema,
} from './adjustments';
import {
  pricingDiagnosticSchema,
  pricingDiagnosticsSchema,
} from './diagnostics';
import { orderedEditIntentsSchema } from './edit-intents';
import {
  exchangeRateSetSchema,
  exchangeRateSourceSchema,
} from './exchange-rates';
import {
  inclusionPolicyResultSchema,
  pricingInclusionPoliciesSchema,
} from './inclusion-policy';
import {
  calculatedMoneyFieldsSchema,
  currencyCodeSchema,
  finiteNumberSchema,
  moneyAmountSchema,
  moneyPrecisionSchema,
  nonNegativeNumberSchema,
  percentageValueSchema,
  vatRateSchema,
} from './money';

export const pricingSourceSchema = z.enum([
  'legacy-price-json',
  'api-database-row',
  'frontend-edit-state',
  'fixture',
  'test',
]);

export const pricingUnitSchema = z.enum([
  'SINGLE',
  'UNIT',
  'DAY',
  'HOUR',
  'NIGHT',
  'PIECE',
  'VEHICLE',
  'PAX',
  'GROUP',
]);

export const singlePriceUnitSchema = z.enum([
  'SINGLE',
  'UNIT',
  'DAY',
  'HOUR',
  'NIGHT',
  'PIECE',
  'VEHICLE',
]);

export const roomPricingModeSchema = z.enum([
  'flat',
  'periods',
  'legacySinglePeriodExpansion',
]);

export const roomPricingSourceMetadataSchema = z.object({
  mode: roomPricingModeSchema,
  sourceSystem: pricingSourceSchema,
  sourcePath: z.string().min(1).nullable().optional(),
  sourceRoomId: z.string().min(1).nullable().optional(),
  sourceHasPricePeriods: z.boolean(),
  originalPeriodCount: z.number().int().nonnegative(),
  originalUnit: z.enum(['UNIT', 'DAY', 'NIGHT']).nullable(),
  legacySinglePeriodExpandedFromRoomPrice: z.boolean(),
  defaultNightCount: z.number().int().nonnegative().nullable(),
});

export const travelerTypeSchema = z.enum([
  'adult',
  'child',
  'teen',
  'baby',
  'senior',
  'unknown',
]);

export const budgetCategorySchema = z.enum([
  'accommodation',
  'activities',
  'dining',
  'logistic',
  'other',
]);

export const travellerMatchStrategySchema = z.enum([
  'travellerId',
  'sourceTravellerId',
  'travellerType',
  'legacyTravellerType',
]);

export const missingTravellerBehaviorSchema = z.enum([
  'zeroPrice',
  'skipPrice',
  'legacyCompatibleFallback',
  // The price lines carry their own authoritative quantities (items with
  // manually-set travellers); not matching an itinerary traveller is
  // expected, so no diagnostic is emitted.
  'useLineQuantity',
]);

export const travellerMatchPolicySchema = z.object({
  strategy: travellerMatchStrategySchema,
  missingTravellerBehavior: missingTravellerBehaviorSchema,
  preserveLegacyMissingTravellerBehavior: z.boolean(),
  sourceSystem: pricingSourceSchema,
  sourcePath: z.string().min(1).nullable().optional(),
});

export const normalizedDateRangeSchema = z.object({
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  nights: z.number().int().nonnegative().nullable(),
});

export const travellerQuantitySchema = z.object({
  travellerId: z.string().min(1),
  type: travelerTypeSchema,
  quantity: z.number().int().nonnegative(),
});

export const travellerInputSchema = z.object({
  id: z.string().min(1),
  sourceId: z.string().min(1).nullable().optional(),
  type: travelerTypeSchema,
  age: z.number().int().nonnegative().nullable(),
  birthDate: z.string().nullable().optional(),
  quantity: z.number().int().nonnegative(),
  label: z.string().nullable().optional(),
});

export const normalizedPriceComponentSchema = z.object({
  currency: currencyCodeSchema,
  cost: finiteNumberSchema.nullable(),
  netCost: finiteNumberSchema.nullable(),
  unitPrice: finiteNumberSchema.nullable(),
  // Base-currency provenance for `unitPrice`: the value the consultant typed
  // (and that the Pricing Table displays) before it was scaled into the item
  // currency. `unitPrice` persists at 2dp, so on a foreign-currency row the
  // typed value cannot be reconstructed by dividing back out — 120 reloads as
  // 119.99 / 120.01 (XT-545). Display-side only: never a price driver, so it
  // must not reach resolveRawUnitPrice or flip markupIsDriver.
  baseUnitPrice: finiteNumberSchema.nullable().optional(),
  finalPrice: finiteNumberSchema.nullable(),
  finalPriceWithTax: finiteNumberSchema.nullable(),
  profit: finiteNumberSchema.nullable(),
  markupPercent: percentageValueSchema.nullable(),
  commissionPercent: percentageValueSchema.nullable(),
  units: nonNegativeNumberSchema.nullable(),
  costVat: vatRateSchema.nullable(),
  priceVat: vatRateSchema.nullable(),
  isGross: z.boolean(),
  useNetCost: z.boolean(),
});

// Item-level traveller categories (legacy `item.travellers`): the set of
// traveller categories + head count shown per row, with add/delete. Distinct
// from per-room distribution (`room.travellers`). Money-neutral metadata —
// never affects pricing. Available on every priceable structure so all item
// types (accommodation, transportation, line items, …) manage categories.
export const accommodationTravellerSchema = z.object({
  travellerId: z.string().min(1),
  type: travelerTypeSchema,
  quantity: z.number().int().nonnegative(),
  label: z.string().nullable().optional(),
});

export const singlePriceStructureSchema = z.object({
  kind: z.literal('single'),
  unit: singlePriceUnitSchema,
  price: normalizedPriceComponentSchema,
  travellers: z.array(accommodationTravellerSchema).optional(),
});

export const roomPricePeriodSchema = z.object({
  id: z.string().min(1).optional(),
  sourceId: z.string().min(1).nullable().optional(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  startNight: z.number().int().nonnegative().nullable(),
  nightCount: z.number().int().nonnegative(),
  price: normalizedPriceComponentSchema,
});

// Per-room traveller distribution (legacy `room.travellers` in price JSON).
// Money-neutral metadata: who sleeps in which room. The legacy engine never
// used it for pricing, and neither does this one.
export const roomTravellerDistributionSchema = z.object({
  travellerId: z.string().min(1),
  quantity: z.number().int().nonnegative(),
  label: z.string().nullable().optional(),
});

export const roomPriceInputSchema = z.object({
  id: z.string().min(1),
  sourceId: z.string().min(1).nullable().optional(),
  roomTypeId: z.string().min(1).nullable().optional(),
  label: z.string().nullable(),
  unit: z.enum(['UNIT', 'DAY', 'NIGHT']),
  pricingMode: roomPricingModeSchema,
  pricingSource: roomPricingSourceMetadataSchema,
  quantity: nonNegativeNumberSchema,
  nights: z.number().int().nonnegative().nullable(),
  price: normalizedPriceComponentSchema,
  pricePeriods: z.array(roomPricePeriodSchema),
  travellers: z.array(roomTravellerDistributionSchema).nullable().optional(),
});

export const roomsPriceStructureSchema = z.object({
  kind: z.literal('rooms'),
  unit: z.enum(['UNIT', 'DAY', 'NIGHT']),
  rooms: z.array(roomPriceInputSchema),
  travellers: z.array(accommodationTravellerSchema).optional(),
});

export const travellerPriceInputSchema = z.object({
  travellerId: z.string().min(1),
  sourceId: z.string().min(1).nullable().optional(),
  type: travelerTypeSchema,
  quantity: z.number().int().nonnegative(),
  price: normalizedPriceComponentSchema,
});

export const travellersPriceStructureSchema = z.object({
  kind: z.literal('travellers'),
  unit: z.literal('PAX'),
  matchPolicy: travellerMatchPolicySchema,
  travellers: z.array(travellerPriceInputSchema),
});

export const groupThresholdInputSchema = z.object({
  id: z.string().min(1).optional(),
  sourceId: z.string().min(1).nullable().optional(),
  travellerId: z.string().min(1).nullable().optional(),
  additionalTravellerId: z.string().min(1).nullable().optional(),
  minTravellers: z.number().int().nonnegative(),
  maxTravellers: z.number().int().nonnegative().nullable(),
  adultPrice: normalizedPriceComponentSchema.nullable(),
  childPrice: normalizedPriceComponentSchema.nullable(),
  additionalAdultPrice: normalizedPriceComponentSchema.nullable(),
  additionalChildPrice: normalizedPriceComponentSchema.nullable(),
});

export const groupFallbackPolicySchema = z.object({
  mode: z.enum(['none', 'legacyDbScalarFallback', 'thresholdOnly']),
  reviewStatus: z.enum(['approved', 'needsReview', 'disabled']),
  source: z.enum([
    'frontendDbScalar',
    'backendDbScalar',
    'legacyPriceJson',
    'fixture',
    'unknown',
  ]),
  fallbackPrice: normalizedPriceComponentSchema.nullable(),
  preserveForInitialMigration: z.boolean(),
  decisionRef: z.string().min(1).nullable().optional(),
});

export const groupPriceStructureSchema = z.object({
  kind: z.literal('group'),
  unit: z.literal('GROUP'),
  thresholds: z.array(groupThresholdInputSchema),
  // Flat group COST (legacy parity): legacy edits one item-level Cost Section
  // for a GROUP while the thresholds control the SELLING price. This component
  // carries that single cost (cost/netCost/VAT/commission); profit for the
  // whole group line is `threshold revenue - this cost`.
  cost: normalizedPriceComponentSchema.nullable().optional(),
  fallbackPolicy: groupFallbackPolicySchema,
  // Per-item traveller categories (legacy `priceRow.travellers`): the group is
  // priced from THESE counts (seeded from the itinerary travellers), so they
  // are editable per row and drive the threshold matching + total.
  travellers: z.array(accommodationTravellerSchema).optional(),
});

export const normalizedPriceStructureSchema = z.discriminatedUnion('kind', [
  singlePriceStructureSchema,
  roomsPriceStructureSchema,
  travellersPriceStructureSchema,
  groupPriceStructureSchema,
]);

export const normalizedItemFlagsSchema = z.object({
  isHidden: z.boolean(),
  isOptional: z.boolean(),
  isLine: z.boolean(),
  isManualLine: z.boolean(),
  isParent: z.boolean(),
  isChild: z.boolean(),
  isSubitem: z.boolean(),
  isDeleted: z.boolean(),
  isExcluded: z.boolean(),
});

export const normalizedItemInputSchema = z.object({
  id: z.string().min(1),
  sourceId: z.string().min(1).nullable().optional(),
  parentId: z.string().min(1).nullable(),
  rootItemId: z.string().min(1).nullable().optional(),
  childIds: z.array(z.string().min(1)),
  // Item-level pricing units from the DB row. Used when a PAX/GROUP
  // structure is converted back to a scalar unit so the legacy round trip
  // (Unit×N → Pax → Unit×N) restores the stored unit count.
  pricingUnits: z.number().int().nonnegative().nullable().optional(),
  title: z.string().nullable(),
  contentCategory: z.string().nullable(),
  contentSubcategory: z.string().nullable(),
  startTime: z.string().nullable().optional(),
  dateRange: normalizedDateRangeSchema.nullable(),
  flags: normalizedItemFlagsSchema,
  priceStructure: normalizedPriceStructureSchema,
  sourceMetadata: z.record(z.string(), z.unknown()).optional(),
});

export const pricingContextSchema = z.object({
  organizationId: z.string().min(1),
  itineraryId: z.string().min(1).nullable(),
  itineraryVersionId: z.string().min(1).nullable(),
  itineraryStartTime: z.string().nullable().optional(),
  // Stored custom day titles (version.day_titles). Day sections use them
  // as labels (legacy pricing table showed/edited them inline).
  dayTitles: z.array(z.string().nullable()).nullable().optional(),
  baseCurrency: currencyCodeSchema,
  source: pricingSourceSchema,
  generatedAt: z.string().nullable().optional(),
});

export const pricingInputSchema = z.object({
  context: pricingContextSchema,
  travellers: z.array(travellerInputSchema),
  travellerQuantities: z.array(travellerQuantitySchema),
  items: z.array(normalizedItemInputSchema),
  exchangeRates: exchangeRateSetSchema,
  inclusionPolicies: pricingInclusionPoliciesSchema,
  editIntents: orderedEditIntentsSchema,
  // Itinerary-level Surcharge / Agency Commission lines. Optional so a payload
  // produced before the blocks shipped (an in-flight client bundle, an older
  // fixture) still validates; the engine reads a missing value as "no lines".
  pricingAdjustments: pricingAdjustmentsInputSchema.optional(),
  moneyPrecision: moneyPrecisionSchema,
  sourceMetadata: z.record(z.string(), z.unknown()).optional(),
});

export const pricingTableRowDisplaySchema = z.object({
  sectionId: z.string().min(1),
  categoryKey: z.string().min(1),
  categoryLabel: z.string().min(1),
  subcategoryKey: z.string().min(1).nullable(),
  subcategoryLabel: z.string().min(1).nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable().optional(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  dateLabel: z.string().nullable().optional(),
  depth: z.number().int().nonnegative(),
  childIds: z.array(z.string().min(1)),
});

export const pricingTableRowSchema = z.object({
  id: z.string().min(1),
  itemId: z.string().min(1),
  parentId: z.string().min(1).nullable(),
  lineId: z.string().min(1).nullable().optional(),
  title: z.string().nullable(),
  priceStructureKind: z.enum(['single', 'rooms', 'travellers', 'group']),
  unit: pricingUnitSchema,
  inclusion: inclusionPolicyResultSchema,
  flags: normalizedItemFlagsSchema,
  quantity: finiteNumberSchema.nullable(),
  money: calculatedMoneyFieldsSchema,
  diagnostics: z.array(pricingDiagnosticSchema),
  display: pricingTableRowDisplaySchema,
  displayMetadata: z.record(z.string(), z.unknown()).optional(),
});

export const pricingTableTotalsSchema = z.object({
  currency: currencyCodeSchema,
  cost: finiteNumberSchema.nullable(),
  netCost: finiteNumberSchema.nullable(),
  finalPrice: finiteNumberSchema.nullable(),
  finalPriceWithTax: finiteNumberSchema.nullable(),
  profit: finiteNumberSchema.nullable(),
  commission: finiteNumberSchema.nullable(),
  markup: finiteNumberSchema.nullable(),
});

export const pricingTableSectionSchema = z.object({
  id: z.string().min(1),
  categoryKey: z.string().min(1),
  categoryLabel: z.string().min(1),
  // 1-based itinerary day for day-grouped sections; lets the UI edit the
  // stored day title without parsing section keys.
  dayIndex: z.number().int().positive().optional(),
  // The stored custom/AI day title alone (without the "Day N - <date>"
  // prefix), so inline editing never writes the date into the title.
  dayTitle: z.string().min(1).optional(),
  rowIds: z.array(z.string().min(1)),
  displayOrder: z.number().int().nonnegative(),
  totals: pricingTableTotalsSchema,
  rowCounts: z.object({
    total: z.number().int().nonnegative(),
    visible: z.number().int().nonnegative(),
    optional: z.number().int().nonnegative(),
    hidden: z.number().int().nonnegative(),
    excluded: z.number().int().nonnegative(),
    lineItems: z.number().int().nonnegative(),
  }),
  diagnosticCounts: z.object({
    total: z.number().int().nonnegative(),
    blocking: z.number().int().nonnegative(),
  }),
});

export const persistenceSafeLineItemMetadataSchema = z.object({
  isLine: z.boolean(),
  isManualLine: z.boolean(),
  startTime: z.string().nullable(),
  parentId: z.string().min(1).nullable(),
  rootItemId: z.string().min(1).nullable(),
});

export const persistenceSafeSingleCalculatedSchema = z.object({
  kind: z.literal('single'),
  unit: singlePriceUnitSchema,
  price: calculatedMoneyFieldsSchema,
  travellers: z.array(accommodationTravellerSchema).optional(),
});

export const persistenceSafeRoomPeriodCalculatedSchema = z.object({
  periodId: z.string().min(1).nullable(),
  sourceId: z.string().min(1).nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  startNight: z.number().int().nonnegative().nullable(),
  nightCount: z.number().int().nonnegative(),
  displayLabel: z.string().min(1).nullable().optional(),
  price: calculatedMoneyFieldsSchema,
});

export const persistenceSafeRoomCalculatedSchema = z.object({
  roomId: z.string().min(1),
  sourceId: z.string().min(1).nullable(),
  roomTypeId: z.string().min(1).nullable(),
  label: z.string().min(1).nullable().optional(),
  unit: z.enum(['UNIT', 'DAY', 'NIGHT']),
  pricingMode: roomPricingModeSchema,
  quantity: finiteNumberSchema.nullable(),
  nights: z.number().int().nonnegative().nullable(),
  price: calculatedMoneyFieldsSchema,
  pricePeriods: z.array(persistenceSafeRoomPeriodCalculatedSchema),
  travellers: z.array(roomTravellerDistributionSchema).nullable().optional(),
});

export const persistenceSafeRoomsCalculatedSchema = z.object({
  kind: z.literal('rooms'),
  unit: z.enum(['UNIT', 'DAY', 'NIGHT']),
  rooms: z.array(persistenceSafeRoomCalculatedSchema),
  travellers: z.array(accommodationTravellerSchema).optional(),
});

export const persistenceSafeTravellerCalculatedSchema = z.object({
  travellerId: z.string().min(1),
  sourceId: z.string().min(1).nullable(),
  label: z.string().min(1).nullable().optional(),
  type: travelerTypeSchema,
  quantity: z.number().int().nonnegative(),
  price: calculatedMoneyFieldsSchema,
});

export const persistenceSafeTravellersCalculatedSchema = z.object({
  kind: z.literal('travellers'),
  unit: z.literal('PAX'),
  matchPolicy: travellerMatchPolicySchema,
  travellers: z.array(persistenceSafeTravellerCalculatedSchema),
});

export const persistenceSafeGroupThresholdCalculatedSchema = z.object({
  thresholdId: z.string().min(1).nullable(),
  sourceId: z.string().min(1).nullable(),
  travellerId: z.string().min(1).nullable(),
  additionalTravellerId: z.string().min(1).nullable(),
  travellerLabel: z.string().min(1).nullable().optional(),
  additionalTravellerLabel: z.string().min(1).nullable().optional(),
  minTravellers: z.number().int().nonnegative(),
  maxTravellers: z.number().int().nonnegative().nullable(),
  adultPrice: calculatedMoneyFieldsSchema.nullable(),
  childPrice: calculatedMoneyFieldsSchema.nullable(),
  additionalAdultPrice: calculatedMoneyFieldsSchema.nullable(),
  additionalChildPrice: calculatedMoneyFieldsSchema.nullable(),
});

export const persistenceSafeGroupCalculatedSchema = z.object({
  kind: z.literal('group'),
  unit: z.literal('GROUP'),
  fallbackPolicy: groupFallbackPolicySchema,
  thresholds: z.array(persistenceSafeGroupThresholdCalculatedSchema),
  // Calculated flat group cost (see `groupPriceStructureSchema.cost`).
  cost: calculatedMoneyFieldsSchema.nullable().optional(),
  travellers: z.array(accommodationTravellerSchema).optional(),
});

export const persistenceSafeCalculatedStructureSchema = z.discriminatedUnion(
  'kind',
  [
    persistenceSafeSingleCalculatedSchema,
    persistenceSafeRoomsCalculatedSchema,
    persistenceSafeTravellersCalculatedSchema,
    persistenceSafeGroupCalculatedSchema,
  ]
);

export const persistenceSafeCalculatedItemSchema = z.object({
  itemId: z.string().min(1),
  lineId: z.string().min(1).nullable().optional(),
  sourceId: z.string().min(1).nullable().optional(),
  priceStructureKind: z.enum(['single', 'rooms', 'travellers', 'group']),
  inclusion: inclusionPolicyResultSchema,
  money: calculatedMoneyFieldsSchema,
  structure: persistenceSafeCalculatedStructureSchema,
  lineItem: persistenceSafeLineItemMetadataSchema,
  roomIds: z.array(z.string().min(1)).optional(),
  travellerIds: z.array(z.string().min(1)).optional(),
  thresholdIds: z.array(z.string().min(1)).optional(),
});

export const persistenceSafeCalculatedFieldsSchema = z.object({
  baseCurrency: currencyCodeSchema,
  versionPrice: moneyAmountSchema.nullable(),
  // `versionPrice` including sell VAT (the customer preview / list figure).
  versionPriceWithTax: moneyAmountSchema.nullable().optional(),
  items: z.array(persistenceSafeCalculatedItemSchema),
  exchangeRateSnapshot: z.array(
    z.object({
      sourceCurrency: currencyCodeSchema,
      targetCurrency: currencyCodeSchema,
      baseCurrency: currencyCodeSchema.nullable().optional(),
      rate: finiteNumberSchema,
      source: exchangeRateSourceSchema,
      locked: z.boolean(),
      sourceRef: z.string().min(1).nullable().optional(),
    })
  ),
});

export const pricingTableOutputSchema = z.object({
  baseCurrency: currencyCodeSchema,
  rows: z.array(pricingTableRowSchema),
  sections: z.array(pricingTableSectionSchema),
  daySections: z.array(pricingTableSectionSchema).optional(),
  // The two evaluated adjustment blocks. Their subtotals are already folded
  // into every entry of `totals` below — never add them again downstream.
  adjustments: pricingAdjustmentsOutputSchema,
  totals: z.object({
    pricingTableTotal: pricingTableTotalsSchema,
    versionPrice: pricingTableTotalsSchema,
    export: pricingTableTotalsSchema,
  }),
  persistenceSafe: persistenceSafeCalculatedFieldsSchema.optional(),
});

export const budgetCategoryOutputSchema = z.object({
  category: budgetCategorySchema,
  total: moneyAmountSchema,
  originalTotal: moneyAmountSchema,
  redistributedAmount: moneyAmountSchema,
  percentage: finiteNumberSchema.nullable(),
  itemIds: z.array(z.string().min(1)),
});

export const budgetCategoryTotalsSchema = z.object({
  accommodation: moneyAmountSchema,
  activities: moneyAmountSchema,
  dining: moneyAmountSchema,
  logistic: moneyAmountSchema,
  other: moneyAmountSchema,
});

export const budgetRedistributionStrategySchema = z.enum([
  'none',
  'activityLogisticRatio',
  'activityLogisticEqualSplit',
  'legacyOtherRedistribution',
]);

export const budgetRedistributionAuditSchema = z.object({
  strategy: budgetRedistributionStrategySchema,
  sourceCategory: budgetCategorySchema.nullable(),
  redistributedAmounts: budgetCategoryTotalsSchema,
  reason: z.string().nullable(),
});

export const budgetMappingReasonSchema = z.enum([
  'contentCategory',
  'contentSubcategory',
  'childItemLogistic',
  'lineItem',
  'hiddenExcluded',
  'optionalExcluded',
  'otherRedistribution',
  'fallbackOther',
  'unknown',
]);

export const budgetMappedItemAuditRowSchema = z.object({
  itemId: z.string().min(1),
  parentId: z.string().min(1).nullable(),
  sourceCategory: z.string().nullable(),
  sourceSubcategory: z.string().nullable(),
  originalCategory: budgetCategorySchema,
  mappedCategory: budgetCategorySchema,
  finalCategory: budgetCategorySchema,
  reasons: z.array(budgetMappingReasonSchema),
  inclusion: inclusionPolicyResultSchema,
  amount: moneyAmountSchema,
  redistributedAmount: moneyAmountSchema.nullable(),
});

export const budgetOutputSchema = z.object({
  baseCurrency: currencyCodeSchema,
  originalCategoryTotals: budgetCategoryTotalsSchema,
  redistribution: budgetRedistributionAuditSchema,
  auditRows: z.array(budgetMappedItemAuditRowSchema),
  categories: z.object({
    accommodation: budgetCategoryOutputSchema,
    activities: budgetCategoryOutputSchema,
    dining: budgetCategoryOutputSchema,
    logistic: budgetCategoryOutputSchema,
    other: budgetCategoryOutputSchema,
  }),
  total: moneyAmountSchema,
});

export const pricingOutputSchema = z.object({
  pricingTable: pricingTableOutputSchema,
  budget: budgetOutputSchema,
  diagnostics: pricingDiagnosticsSchema,
});
